import ls from '@/helpers/localStorage';
import api from '@/services/api';
import * as firebaseClient from '@/services/notifications/firebase';
// import constants from '@/constants';

export const DEFAULT_ORDER_BOOK = {
  noOrders: 0,
  yesOrders: 0,
};

function makeTradeOptions() {
  return {
    contractId: null,
    operationType: 'buy', // buy | sell
    operationValue: 'yes', // yes | no
    pricePerShare: null,
    skipConfirmation: ls.get('skipConfirmation', false),
    quantity: null,
  };
}

// Values the user set in the order slip: a quantity, which carries across a YES/NO switch, and a price,
// which stays with its side (prices holds the user's price for each side while the other side is shown).
function makeUserTradeValues() {
  return {
    price: false,
    quantity: false,
    prices: { yes: null, no: null },
  };
}

function getPricePerShare(value) {
  const min = 1;
  const max = 99;
  let price = parseInt(value, 10);

  if (price < min) {
    price = min;
  }
  if (price > max) {
    price = max;
  }
  return price;
}

const KEY_BOTTOM_WIDGET_FOOTER_MODE = 'bottom-widget-footer-mode';
export const BOTTOM_WIDGET_FOOTER_MODES = {
  BETSLIP: 'betslip',
  PINNED: 'pinned',
  TOGGlE: 'toggle',
  QUICK_SWITCH: 'quick-switch',
};
const BOTTOM_WIDGET_DEFAULT_MODE = BOTTOM_WIDGET_FOOTER_MODES.BETSLIP;
const BOTTOM_WIDGET_KEYS_TO_STORE_IN_LS = [
  BOTTOM_WIDGET_FOOTER_MODES.BETSLIP,
  BOTTOM_WIDGET_FOOTER_MODES.PINNED,
  BOTTOM_WIDGET_FOOTER_MODES.QUICK_SWITCH,
];

function getStoredBottomWidgetFooterMode() {
  const storedMode = ls.get(KEY_BOTTOM_WIDGET_FOOTER_MODE, null);

  if (BOTTOM_WIDGET_KEYS_TO_STORE_IN_LS.includes(storedMode)) {
    return storedMode;
  }

  return null;
}

const STORED_BOTTOM_WIDGET_FOOTER_MODE = getStoredBottomWidgetFooterMode();
const INITIAL_BOTTOM_WIDGET_FOOTER_MODE =
  STORED_BOTTOM_WIDGET_FOOTER_MODE || BOTTOM_WIDGET_DEFAULT_MODE;

const REMEMBER_TRADE_DIRECTION = true;
const PREFER_GLOBAL_TRADE_DIRECTION = false;
const GLOBAL_TRADE_DIRECTION_KEY = '__global__';
function tradeDirectionKey(marketId) {
  if (!REMEMBER_TRADE_DIRECTION) return null;
  return PREFER_GLOBAL_TRADE_DIRECTION ? GLOBAL_TRADE_DIRECTION_KEY : marketId;
}

export default {
  namespaced: true,
  getters: {
    tradeDirectionForMarket: (state) => (marketId) =>
      state.marketTradeDirections[tradeDirectionKey(marketId)],
  },
  state: {
    orderBook: null,
    orderBookRequestVersion: 0,
    hidePurchaseForContractId: null,
    contractShares: [],
    contractOffers: [],
    tradeOptions: makeTradeOptions(),
    userTradeValues: makeUserTradeValues(),
    masterTradeOptions: {},
    marketTradeDirections: {},
    tradeSuccess: false,
    keyboardDoubleSelected: false,
    cancelToggle: false,
    tradeOperationToggle: null,
    isInConfirmationMode: false,
    confirmationDoneData: null,
    confirmationData: null,
    bottomBetslipExtended: false,
    bottomWidgetFooterMode: INITIAL_BOTTOM_WIDGET_FOOTER_MODE,
    bottomWidgetFooterModeContractsSection: INITIAL_BOTTOM_WIDGET_FOOTER_MODE,
    previousBottomWidgetFooterMode: INITIAL_BOTTOM_WIDGET_FOOTER_MODE,
    hasStoredBottomWidgetFooterModePreference: STORED_BOTTOM_WIDGET_FOOTER_MODE !== null,
  },
  mutations: {
    toggleHidePurchaseForContractId(state, contractId) {
      if (state.hidePurchaseForContractId === null) {
        state.hidePurchaseForContractId = contractId;
      } else {
        state.hidePurchaseForContractId = null;
      }
    },
    setOrderBook(state, payload) {
      state.orderBook = payload;
    },
    clearOrderBook(state) {
      state.orderBook = null;
    },
    invalidateOrderBookRequests(state) {
      state.orderBookRequestVersion += 1;
    },
    setContractShares(state, payload) {
      state.contractShares = payload;
    },
    setContractOffers(state, payload) {
      state.contractOffers = payload;
    },
    setTradeOptions(state, payload) {
      if (
        state.tradeOptions !== null &&
        payload !== null &&
        state.tradeOptions.contractId === payload.contractId &&
        state.tradeOptions.operationType === payload.operationType &&
        state.tradeOptions.operationValue === payload.operationValue
      ) {
        state.tradeOptions = {
          ...state.tradeOptions,
          ...payload,
        };
      } else {
        const defaults = makeTradeOptions();
        const previous = state.tradeOptions;
        let pricePerShare = defaults.pricePerShare;
        let quantity = defaults.quantity;
        let skipConfirmation = defaults.skipConfirmation;
        let userTradeValues = makeUserTradeValues();
        if (payload && previous.contractId === payload.contractId) {
          skipConfirmation = previous.skipConfirmation || false;
          // YES <-> NO: keep the user's quantity and the price they set on the new side. Anything left null
          // is filled with the new side's default by the order slip, never with the other side's price.
          if (previous.operationType === payload.operationType) {
            const user = state.userTradeValues;
            const prices = {
              ...user.prices,
              [previous.operationValue]: user.price ? previous.pricePerShare : null,
            };
            pricePerShare = prices[payload.operationValue] ?? null;
            quantity = user.quantity ? previous.quantity : null;
            userTradeValues = { price: pricePerShare != null, quantity: user.quantity, prices };
          }
        }
        state.userTradeValues = userTradeValues;
        state.tradeOptions = {
          ...defaults,
          quantity,
          ...payload,
          pricePerShare,
          skipConfirmation,
        };
      }

      if (state.tradeOptions && state.tradeOptions.contractId) {
        state.hidePurchaseForContractId = null;
      }
    },
    setTradeSuccess(state, flag) {
      state.tradeSuccess = flag;
    },
    setTradeSkipConfirmation(state, skipConfirmation) {
      const value = !!skipConfirmation;
      const expiresAt = 31536000; // 1 year
      ls.set('skipConfirmation', value, expiresAt);
      state.tradeOptions = { ...state.tradeOptions, skipConfirmation: value };
    },
    updateKeyboardDoubleSelected(state, selected) {
      state.keyboardDoubleSelected = selected;
    },
    setCancelToggle(state, flag) {
      state.cancelToggle = flag;
    },
    setIsInConfirmationMode(state, flag) {
      state.isInConfirmationMode = flag;
    },
    setConfirmationData(state, data) {
      state.confirmationData = data;
    },
    setConfirmationDoneData(state, data) {
      state.confirmationDoneData = data;
    },
    toggleTradeOperation(state, side) {
      state.tradeOperationToggle = side;
    },
    setBottomWidgetFooterMode(state, value) {
      state.previousBottomWidgetFooterMode = state.bottomWidgetFooterMode;
      state.bottomWidgetFooterMode = value;
    },
    toggleBottomBetslipMode(state) {
      state.bottomBetslipExtended = !state.bottomBetslipExtended;
    },
    setBottomWidgetFooterModeContractsSection(state, value) {
      if (BOTTOM_WIDGET_KEYS_TO_STORE_IN_LS.includes(value)) {
        ls.set(KEY_BOTTOM_WIDGET_FOOTER_MODE, value);
        state.hasStoredBottomWidgetFooterModePreference = true;
      }
      state.bottomWidgetFooterModeContractsSection = value;
    },
    setTradeQuantity(state, quantity) {
      state.tradeOptions = { ...state.tradeOptions, quantity: parseInt(quantity, 10) || null };
    },
    setTradePrice(state, pricePerShare) {
      state.tradeOptions = { ...state.tradeOptions, pricePerShare };
    },
    setMasterTradeOptions(state, data) {
      state.masterTradeOptions = data;
    },
    setUserTradeValues(state, { price, quantity }) {
      state.userTradeValues = {
        ...state.userTradeValues,
        ...(price !== undefined && { price }),
        ...(quantity !== undefined && { quantity }),
      };
    },
    clearUserTradeValues(state) {
      state.userTradeValues = makeUserTradeValues();
    },
    setBottomBetSlipExtendedMode(state, extended) {
      state.bottomBetslipExtended = extended;
    },
    setMarketTradeDirection(state, { marketId, value }) {
      const key = tradeDirectionKey(marketId);
      if (key == null) return;
      state.marketTradeDirections = { ...state.marketTradeDirections, [key]: value };
    },
  },
  actions: {
    confirmTrade({ commit }, payload) {
      return api.trade.postConfirmTrade(payload);
    },
    submitTrade({ commit }, payload) {
      return api.trade.postSubmitTrade(payload);
    },
    getOrderBook({ commit, state }, contractId) {
      const requestVersion = state.orderBookRequestVersion;
      return api.trade.getOrderBook(contractId).then((response) => {
        if (requestVersion === state.orderBookRequestVersion) commit('setOrderBook', response.data);
        return response.data;
      });
    },
    updateOrderBookPeriodically({ commit }, contractId) {
      return firebaseClient.addOrderBookListener(
        contractId,
        (data) => {
          commit('setOrderBook', data);
        },
        Object.create(DEFAULT_ORDER_BOOK),
      );
    },
    clearOrderBookUpdates(_context, { contractId, callback }) {
      firebaseClient.clearOrderBookListeners(contractId, callback);
    },
    clearOrderBook({ commit }) {
      commit('clearOrderBook');
    },
    getContractShares({ commit, state }, contractId) {
      if (contractId === null) {
        commit('setContractShares', []);
        return Promise.resolve([]);
      }

      const requestVersion = state.orderBookRequestVersion;
      return api.profile.getContractShares(contractId).then((response) => {
        if (requestVersion === state.orderBookRequestVersion)
          commit('setContractShares', response.data.shares);
        return response.data.shares;
      });
    },
    getContractOffers({ commit, state }, contractId) {
      if (contractId === null) {
        commit('setContractOffers', []);
        return Promise.resolve([]);
      }

      const requestVersion = state.orderBookRequestVersion;
      return api.profile.getContractOffers(contractId).then((response) => {
        if (requestVersion === state.orderBookRequestVersion)
          commit('setContractOffers', response.data.offers);
        return response.data.offers;
      });
    },
    cancelAllOffers({ commit }, payload) {
      const contractId = payload.contractId;
      delete payload.contractId;
      return api.trade.postCancelAllOffers(contractId, payload).then((response) => {
        commit('setCancelToggle', true);
        return response.data;
      });
    },
    cancelOffer({ commit }, offerId) {
      return api.trade.postCancelOffer(offerId).then((response) => {
        commit('setCancelToggle', true);
        return response.data;
      });
    },
    setCancelToggle({ commit }, flag) {
      commit('setCancelToggle', flag);
    },
    setIsInConfirmationMode({ commit }, flag) {
      commit('setIsInConfirmationMode', flag);
    },
    setConfirmationData({ commit }, data) {
      commit('setConfirmationData', data);
    },
    setConfirmationDoneData({ commit }, data) {
      commit('setConfirmationDoneData', data);
    },
    setTradeOptions({ commit }, payload) {
      if (payload === null) {
        commit('setConfirmationData', null);
        commit('setConfirmationDoneData', null);
      }
      commit('setTradeOptions', payload);
    },
    setTradeQuantity({ commit, state }, quantity) {
      commit('setTradeQuantity', quantity);
      commit('setMasterTradeOptions', {
        quantity,
        pricePerShare: state.tradeOptions.pricePerShare,
      });
    },
    setTradePrice({ commit, state }, pricePerShare) {
      const standardValue = getPricePerShare(pricePerShare);
      commit('setMasterTradeOptions', {
        pricePerShare: standardValue,
        quantity: state.tradeOptions.quantity,
      });
      commit('setTradePrice', standardValue);
    },
    setMasterTradeOptions({ commit }, { quantity, pricePerShare }) {
      commit('setMasterTradeOptions', { quantity, pricePerShare });
    },
    setUserTradeValues({ commit }, values) {
      commit('setUserTradeValues', values);
    },
    clearUserTradeValues({ commit }) {
      commit('clearUserTradeValues');
    },
    setTradeSuccess({ commit }, flag) {
      commit('setTradeSuccess', flag);
    },
    setTradeSkipConfirmation({ commit }, skipConfirmation) {
      commit('setTradeSkipConfirmation', skipConfirmation);
    },
    toggleTradeOperation({ commit }, side) {
      commit('toggleTradeOperation', side);
    },
    updateKeyboardDoubleSelected({ commit }, selected) {
      commit('updateKeyboardDoubleSelected', selected);
    },
    getOrderBookTradeInfo({ commit }, { contractId, tradeType, price }) {
      return api.trade
        .getOrderBookTradeInfo(contractId, tradeType, price)
        .then((response) => response.data);
    },
    setBottomWidgetFooterMode({ commit }, value) {
      commit('setBottomWidgetFooterMode', value);
    },
    toggleHidePurchaseForContractId({ commit }, contractId) {
      commit('toggleHidePurchaseForContractId', contractId);
    },
    toggleBottomBetslipMode({ commit }) {
      commit('toggleBottomBetslipMode');
    },
    setBottomBetSlipExtendedMode({ commit }, value) {
      commit('setBottomBetSlipExtendedMode', value);
    },
    setBottomWidgetFooterModeContractsSection({ commit }, value) {
      commit('setBottomWidgetFooterModeContractsSection', value);
    },
    setMarketTradeDirection({ commit }, payload) {
      commit('setMarketTradeDirection', payload);
    },
  },
};
